import React, { useEffect, useState } from "react";
import { MdDelete, MdEvent, MdPersonOff, MdInbox } from "react-icons/md";
import {
  addHoliday,
  deleteHoliday,
  getHolidays,
  getDayOverrides,
  upsertDayOverride,
  deleteDayOverride,
} from "../../../../services/capacityApi";
import {
  DayCapacityOverrideViewModel,
  HolidayViewModel,
} from "../../../../interfaces/Capacity";

interface Option {
  id: number;
  name: string;
}

interface Props {
  employees: Option[];
  onChanged: () => void;
}

const labelCls = "mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300";

const fieldCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const primaryBtnCls =
  "dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50";

const tableWrapCls =
  "overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800";

const thCls =
  "px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300";

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");

const CapacityCalendar: React.FC<Props> = ({ employees, onChanged }) => {
  // Feriados
  const [holidays, setHolidays] = useState<HolidayViewModel[]>([]);
  const [holDate, setHolDate] = useState("");
  const [holDesc, setHolDesc] = useState("");

  // Capacity por dia
  const [dayOverrides, setDayOverrides] = useState<DayCapacityOverrideViewModel[]>([]);
  const [ovEmployee, setOvEmployee] = useState("");
  const [ovDate, setOvDate] = useState("");
  const [ovHours, setOvHours] = useState("");
  const [ovReason, setOvReason] = useState("");

  const reloadHolidays = () => getHolidays().then(setHolidays);
  const reloadDayOverrides = () => getDayOverrides(undefined, 120).then(setDayOverrides);

  useEffect(() => {
    reloadHolidays();
    reloadDayOverrides();
  }, []);

  const submitHoliday = async () => {
    if (!holDate) return;
    await addHoliday({ date: holDate, description: holDesc || undefined });
    setHolDate("");
    setHolDesc("");
    reloadHolidays();
    onChanged();
  };

  const submitOverride = async () => {
    if (!ovEmployee || !ovDate || ovHours === "") return;
    await upsertDayOverride({
      employeeId: Number(ovEmployee),
      date: ovDate,
      capacityHours: Number(ovHours),
      reason: ovReason || undefined,
    });
    setOvHours("");
    setOvReason("");
    reloadDayOverrides();
    onChanged();
  };

  return (
    <div className="flex flex-col gap-8">
      {/* ── Feriados / dias sem expediente ─────────────────────────── */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <MdEvent className="h-5 w-5 text-dv-blue-600 dark:text-dv-cyan-300" aria-hidden="true" />
          <h4 className="text-sm font-bold text-navy-700 dark:text-white">
            Feriados e dias sem expediente (empresa)
          </h4>
        </div>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          Datas em que o time todo não trabalha. A capacity desses dias é zerada no cálculo.
        </p>

        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label htmlFor="hol-date" className={labelCls}>
              Data <span className="text-red-500">*</span>
            </label>
            <input
              id="hol-date"
              type="date"
              className={fieldCls}
              value={holDate}
              onChange={(e) => setHolDate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="hol-desc" className={labelCls}>
              Descrição
            </label>
            <input
              id="hol-desc"
              placeholder="Descrição (ex.: Natal)"
              className={fieldCls}
              value={holDesc}
              onChange={(e) => setHolDesc(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <button type="button" onClick={submitHoliday} className={primaryBtnCls}>
              Adicionar
            </button>
          </div>
        </div>

        <div className={tableWrapCls}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-navy-900">
              <tr>
                <th className={thCls}>Data</th>
                <th className={thCls}>Descrição</th>
                <th className={`${thCls} w-10`}></th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr
                  key={h.id}
                  className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                >
                  <td className="px-4 py-3 font-medium tabular-nums text-navy-700 dark:text-white">{fmtDate(h.date)}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{h.description ?? "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      aria-label="Excluir feriado"
                      onClick={async () => {
                        await deleteHoliday(h.id);
                        reloadHolidays();
                        onChanged();
                      }}
                      className="text-gray-500 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    >
                      <MdDelete className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
              {holidays.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-700 dark:text-gray-300">
                      <MdInbox className="h-6 w-6 text-gray-400" aria-hidden="true" />
                      Nenhum feriado cadastrado.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Capacity por dia (colaborador) ─────────────────────────── */}
      <section>
        <div className="mb-2 flex items-center gap-2">
          <MdPersonOff className="h-5 w-5 text-dv-blue-600 dark:text-dv-cyan-300" aria-hidden="true" />
          <h4 className="text-sm font-bold text-navy-700 dark:text-white">
            Capacity de um dia específico / falta justificada
          </h4>
        </div>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          Define a capacity de um colaborador num dia específico. Use <strong>0h</strong> para uma falta
          justificada (dia inteiro) ou um valor parcial (ex.: 4h para meio período).
        </p>

        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="day-employee" className={labelCls}>
              Colaborador <span className="text-red-500">*</span>
            </label>
            <select
              id="day-employee"
              className={fieldCls}
              value={ovEmployee}
              onChange={(e) => setOvEmployee(e.target.value)}
            >
              <option value="">Colaborador…</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="day-date" className={labelCls}>
              Data <span className="text-red-500">*</span>
            </label>
            <input
              id="day-date"
              type="date"
              className={fieldCls}
              value={ovDate}
              onChange={(e) => setOvDate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="day-hours" className={labelCls}>
              Horas <span className="text-red-500">*</span>
            </label>
            <input
              id="day-hours"
              type="number"
              min={0}
              step={0.5}
              placeholder="Horas"
              className={fieldCls}
              value={ovHours}
              onChange={(e) => setOvHours(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="day-reason" className={labelCls}>
              Motivo
            </label>
            <input
              id="day-reason"
              placeholder="Motivo (opcional)"
              className={fieldCls}
              value={ovReason}
              onChange={(e) => setOvReason(e.target.value)}
            />
          </div>
          <div className="flex items-end sm:col-span-2 lg:col-span-4">
            <button type="button" onClick={submitOverride} className={primaryBtnCls}>
              Salvar
            </button>
          </div>
        </div>

        <div className={tableWrapCls}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-navy-900">
              <tr>
                <th className={thCls}>Colaborador</th>
                <th className={thCls}>Data</th>
                <th className={`${thCls} text-right`}>Capacity</th>
                <th className={thCls}>Motivo</th>
                <th className={`${thCls} w-10`}></th>
              </tr>
            </thead>
            <tbody>
              {dayOverrides.map((o) => (
                <tr
                  key={o.id}
                  className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                >
                  <td className="px-4 py-3 text-navy-700 dark:text-white">{o.employeeName}</td>
                  <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-300">{fmtDate(o.date)}</td>
                  <td
                    className={`px-4 py-3 text-right font-medium tabular-nums ${
                      o.capacityHours === 0
                        ? "text-red-600 dark:text-red-400"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {o.capacityHours}h
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{o.reason ?? "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      aria-label="Excluir ajuste de dia"
                      onClick={async () => {
                        await deleteDayOverride(o.id);
                        reloadDayOverrides();
                        onChanged();
                      }}
                      className="text-gray-500 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    >
                      <MdDelete className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
              {dayOverrides.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-700 dark:text-gray-300">
                      <MdInbox className="h-6 w-6 text-gray-400" aria-hidden="true" />
                      Nenhum ajuste de dia cadastrado.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default CapacityCalendar;
